import { DefaultError, useMutation, useQuery } from '@tanstack/react-query';
import { useQueryToast } from 'hooks';
import i18next from 'i18next';
import { apiClient } from 'utils';
import { IResponseHandlerOptions, useResponseHandler } from 'utils/service/responseHandler';
import {
  ICreateDigitalDistribution,
  IUpdateDigitalDistribution,
} from './types';
import { IDistributionResponse, ITemplatesLightListItem } from '../types';

export const useGetDigitalDistributionTemplateLightList = (enabled?: boolean) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ITemplatesLightListItem[], DefaultError>({
    queryKey: ['digital-distribution-template-light-list'],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `DigitalDistributionSectionTemplate/LightList`,
      });

      return responseHandler(response, {
        groupName: `useGetDigitalDistributionTemplateLightList`,
        notFoundPath: '/templates',
      });
    },
    enabled: enabled,
  });
};

export const useGetDigitalDistributionSectionTemplate =  (
  id: string,
  options?: IResponseHandlerOptions<IDistributionResponse>,
  enabled?: boolean,
  retry?: boolean
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IDistributionResponse, DefaultError>({
    queryKey: ['digital-distribution-template', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `DigitalDistributionSectionTemplate/${id}`,
      });

      return responseHandler(response, {
        groupName: `useGetDigitalDistributionSectionTemplate - ${id}`,
        notFoundPath: '/templates',
        ...options,
      });
    },
    retry,
    enabled,
  });
};

export const usePostDigitalDistributionTemplate = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePostDigitalDistributionTemplate'
  );
  return useMutation<string, DefaultError, ICreateDigitalDistribution>({
    async mutationFn(body) {
      loading(
        i18next.t('service-message.create.loading', {
          what: body.title,
        })
      );
      const response = await apiClient({
        method: 'POST',
        url: 'DigitalDistributionSectionTemplate',
        body: { digitalDistributionSectionTemplate: body },
      });
      
      return responseHandler(response, {
        groupName: 'usePostDigitalDistributionTemplate',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: '',
        })
      );
    },
    onError: dismiss,
  });
};

export const usePutDigitalDistributionTemplate = (id: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePutDigitalDistributionTemplate'
  );
  return useMutation<string, DefaultError, IUpdateDigitalDistribution>({
    async mutationFn(body) {
      loading(
        i18next.t('service-message.create.loading', {
          what: body.title,
        })
      );
      const response = await apiClient({
        method: 'PUT',
        url: 'DigitalDistributionSectionTemplate',
        body: {digitalDistributionSectionId: id, digitalDistributionSectionTemplate: body},
      });

      return responseHandler(response, {
        groupName: 'usePutDigitalDistributionTemplate',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: '',
        })
      );
    },
    onError: dismiss,
  });
};

export const useDeleteDigitalDistributionTemplate = (id: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'useDeleteDigitalDistributionTemplate'
  );
  return useMutation<string, DefaultError>({
    async mutationFn() {
      loading(
        i18next.t('service-message.remove.loading', {
          what: '',
        })
      );
      const response = await apiClient({
        method: 'DELETE',
        url: `DigitalDistributionSectionTemplate/${id}`,
      });

      return responseHandler(response, {
        groupName: 'useDeleteDigitalDistributionTemplate',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.remove.success', {
          what: '',
        })
      );
    },
    onError: dismiss,
  });
};
